import { SearchOutlined } from '@ant-design/icons';
import { Select } from 'antd5';
import { snakeCase } from 'lodash';
import type { BaseSelectRef } from 'rc-select';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { filterOption } from 'components/choosers/helpers';
import { CustomFilterContainer, CustomFilterPopover } from 'components/data/helpers/filters.style';
import {
  getTableSiteFilterIds,
  getTableTurbineFilterIds,
} from 'components/data/helpers/tableFilterUtils';
import { DataTableContext } from 'components/DataTable';
import {
  FilterMultiSelectContainer,
  handleTableFilterActionMouseDown,
  TableFilterMultiSelect,
} from 'components/DataTable/customFilters/TableFilterMultiSelect';
import { Button, Tooltip } from 'components/ui';
import { AutoFocus } from 'horizon/components/AutoFocus';
import type { CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import {
  AtlasGqlFilterOperator,
  AtlasGqlSortDirection,
  useGetAssetTypesMinimalQuery,
  useGetSubComponentOptionsForChooserQuery,
} from 'types/atlas-graphql';

type MultiFilterValue = { key: string; value: string[] };

const FilterActions = styled.div.attrs({ className: 'ant-table-filter-dropdown-btns' })`
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

const StackedFilterField = styled(FilterMultiSelectContainer)`
  width: 100%;

  & + & {
    margin-top: 8px;
  }
`;

const getFilterValues = (
  selectedKeys: (MultiFilterValue | string[])[] | undefined,
  filterKey: string
): string[] => {
  if (!selectedKeys?.length) {
    return [];
  }

  if (typeof selectedKeys[0] === 'object' && 'key' in selectedKeys[0]) {
    return (selectedKeys as MultiFilterValue[]).find(({ key }) => key === filterKey)?.value ?? [];
  }

  return filterKey === 'asset' ? (selectedKeys as string[]) : [];
};

export const WorkOrderComponentFilter = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}: CustomFilterDropdownProps) => {
  const { t } = useTranslation();
  const componentTypeRef = useRef<BaseSelectRef>(null);
  const componentRef = useRef<BaseSelectRef>(null);

  const { filteredInfo } = useContext(DataTableContext);
  const siteSelected = getTableSiteFilterIds(filteredInfo).length > 0;
  const turbineIds = getTableTurbineFilterIds(filteredInfo);
  const turbineSelected = siteSelected && turbineIds.length > 0;

  const [componentTypes, setComponentTypes] = useState<string[]>(() =>
    getFilterValues(selectedKeys, 'componentType')
  );
  const [componentIds, setComponentIds] = useState<string[]>(() =>
    getFilterValues(selectedKeys, 'asset')
  );
  const [componentTypeSearch, setComponentTypeSearch] = useState('');
  const [componentSearch, setComponentSearch] = useState('');

  // Keep local selections in sync with the applied filter. selectedKeys can change
  // externally - e.g. clearStaleAssetHierarchyFilters removes this filter when the
  // Site/Turbine filter changes - and without this the dropdown would keep (and on OK
  // re-apply) a stale Component that belonged to a previously selected Turbine.
  useEffect(() => {
    setComponentTypes(getFilterValues(selectedKeys, 'componentType'));
    setComponentIds(getFilterValues(selectedKeys, 'asset'));
  }, [selectedKeys]);

  const { data: typesData, loading: typesLoading } = useGetAssetTypesMinimalQuery({
    skip: !turbineSelected,
    variables: {
      excludeUninstantiated: true,
    },
  });

  const sortedComponentTypes = useMemo(() => {
    if (!typesData?.assetTypes) {
      return [];
    }

    return typesData.assetTypes
      .filter(({ name }) => !['Site', 'Turbine'].includes(name))
      .sort((a, b) => (a?.name < b?.name ? -1 : 1));
  }, [typesData]);

  const { data: componentsData, loading: componentsLoading } =
    useGetSubComponentOptionsForChooserQuery({
      skip: !turbineSelected || !componentTypes.length,
      variables: {
        filterBy: [
          {
            key: 'assetTypeNames',
            values: componentTypes,
            operator: AtlasGqlFilterOperator.Equals,
          },
          {
            key: 'childrenOfIds',
            values: turbineIds,
            operator: AtlasGqlFilterOperator.Equals,
          },
        ],
        sortBy: [
          {
            key: 'typeName',
            sort: AtlasGqlSortDirection.Asc,
          },
        ],
      },
    });

  const handleComponentTypeChange = (value: string[]) => {
    setComponentTypes(value);
    setComponentIds([]);
    setComponentSearch('');
  };

  const handleConfirm = () => {
    const nextFilters: MultiFilterValue[] = [];

    if (componentTypes.length) {
      nextFilters.push({ key: 'componentType', value: componentTypes });
    }

    if (componentIds.length) {
      nextFilters.push({ key: 'asset', value: componentIds });
    }

    setComponentTypeSearch('');
    setComponentSearch('');
    setSelectedKeys(nextFilters);
    confirm({ closeDropdown: true });
  };

  // clearFilters only resets the draft; confirm applies it.
  const handleReset = () => {
    setComponentTypes([]);
    setComponentIds([]);
    setComponentTypeSearch('');
    setComponentSearch('');
    clearFilters?.();
    confirm({ closeDropdown: true });
  };

  return (
    <AutoFocus elementToFocus={componentTypeRef}>
      <CustomFilterPopover className="ant-table-filter-dropdown" $width="400px">
        <CustomFilterContainer>
          <StackedFilterField>
            <Tooltip
              title={
                !turbineSelected
                  ? t(
                      'workOrder.setTurbineFilterBeforeComponentType',
                      'Set turbine filter before setting component type filter.'
                    )
                  : undefined
              }
            >
              <TableFilterMultiSelect
                id="work-order-component-type-filter-dropdown"
                ref={componentTypeRef}
                listHeight={160}
                placeholder={t('table.selectComponentType', 'Select Component Type...')}
                loading={typesLoading}
                disabled={!turbineSelected}
                allowClear
                showSearch
                filterOption={filterOption}
                virtual={false}
                value={componentTypes}
                onChange={handleComponentTypeChange}
                searchValue={componentTypeSearch}
                onSearch={setComponentTypeSearch}
                onOpenChange={open => {
                  if (!open) {
                    setComponentTypeSearch('');
                  }
                }}
                suffixIcon={<SearchOutlined aria-hidden />}
              >
                {sortedComponentTypes.map(component => (
                  <Select.Option
                    key={component.name}
                    className="work-order-component-type-filter-option"
                    value={snakeCase(component.name)}
                  >
                    {component.name}
                  </Select.Option>
                ))}
              </TableFilterMultiSelect>
            </Tooltip>
          </StackedFilterField>
          <StackedFilterField>
            <Tooltip
              title={
                !turbineSelected
                  ? t(
                      'workOrder.setTurbineFilterBeforeComponent',
                      'Set turbine filter before setting component filter.'
                    )
                  : !componentTypes.length
                    ? t(
                        'workOrder.setComponentTypeFilterBeforeComponent',
                        'Set component type filter before setting component filter.'
                      )
                    : undefined
              }
            >
              <TableFilterMultiSelect
                id="work-order-component-filter-dropdown"
                ref={componentRef}
                listHeight={160}
                placeholder={t('table.selectComponent', 'Select Component...')}
                loading={componentsLoading}
                disabled={!turbineSelected || !componentTypes.length}
                allowClear
                showSearch
                filterOption={filterOption}
                virtual={false}
                value={componentIds}
                onChange={setComponentIds}
                searchValue={componentSearch}
                onSearch={setComponentSearch}
                onOpenChange={open => {
                  if (!open) {
                    setComponentSearch('');
                  }
                }}
                suffixIcon={<SearchOutlined aria-hidden />}
              >
                {componentsData?.assetsWithMetadata?.items?.map(asset => (
                  <Select.Option
                    key={asset.id}
                    className="work-order-component-filter-option"
                    value={asset.id}
                  >
                    {asset.uniqueIdLabel}
                  </Select.Option>
                ))}
              </TableFilterMultiSelect>
            </Tooltip>
          </StackedFilterField>
        </CustomFilterContainer>
        <FilterActions>
          <Button
            type="link"
            size="small"
            onMouseDown={event => handleTableFilterActionMouseDown(event, handleReset)}
          >
            {t('table.reset', 'Reset')}
          </Button>
          <Button
            type="primary"
            size="small"
            onMouseDown={event => handleTableFilterActionMouseDown(event, handleConfirm)}
          >
            {t('table.ok', 'OK')}
          </Button>
        </FilterActions>
      </CustomFilterPopover>
    </AutoFocus>
  );
};
